/*
 * Vencord, a modification for Discord's desktop app
 * Copyright (c) 2023 Vendicated and contributors
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
*/

/*
To generate:
- Visit https://translate.google.com/?sl=auto&tl=en&op=translate
- Open Language dropdown
- Open Devtools and use the element picker to pick the root of the language picker
- Right click on the element in devtools and click "Store as global variable"

copy(Object.fromEntries(
    Array.from(
        temp1.querySelectorAll("[data-language-code]"),
        e => [e.dataset.languageCode, e.children[1].textContent]
    ).sort((a, b) => a[1] === "Detect language" ? -1 : b[1] === "Detect language" ? 1 : a[1].localeCompare(b[1]))
))
*/

export type GoogleLanguage = keyof typeof GoogleLanguages;
export type DeeplLanguage = keyof typeof DeeplLanguages;
export type KagiLanguage = keyof typeof KagiLanguages;

export const GoogleLanguages = {
    "auto": "Detect language",
    "af": "Afrikaans",
    "sq": "Albanian",
    "am": "Amharic",
    "ar": "Arabic",
    "hy": "Armenian",
    "as": "Assamese",
    "ay": "Aymara",
    "az": "Azerbaijani",
    "bm": "Bambara",
    "eu": "Basque",
    "be": "Belarusian",
    "bn": "Bengali",
    "bho": "Bhojpuri",
    "bs": "Bosnian",
    "bg": "Bulgarian",
    "ca": "Catalan",
    "ceb": "Cebuano",
    "ny": "Chichewa",
    "zh-CN": "Chinese (Simplified)",
    "zh-TW": "Chinese (Traditional)",
    "co": "Corsican",
    "hr": "Croatian",
    "cs": "Czech",
    "da": "Danish",
    "dv": "Dhivehi",
    "doi": "Dogri",
    "nl": "Dutch",
    "en": "English",
    "eo": "Esperanto",
    "et": "Estonian",
    "ee": "Ewe",
    "tl": "Filipino",
    "fi": "Finnish",
    "fr": "French",
    "fy": "Frisian",
    "gl": "Galician",
    "ka": "Georgian",
    "de": "German",
    "el": "Greek",
    "gn": "Guarani",
    "gu": "Gujarati",
    "ht": "Haitian Creole",
    "ha": "Hausa",
    "haw": "Hawaiian",
    "iw": "Hebrew",
    "hi": "Hindi",
    "hmn": "Hmong",
    "hu": "Hungarian",
    "is": "Icelandic",
    "ig": "Igbo",
    "ilo": "Ilocano",
    "id": "Indonesian",
    "ga": "Irish",
    "it": "Italian",
    "ja": "Japanese",
    "jw": "Javanese",
    "kn": "Kannada",
    "kk": "Kazakh",
    "km": "Khmer",
    "rw": "Kinyarwanda",
    "gom": "Konkani",
    "ko": "Korean",
    "kri": "Krio",
    "ku": "Kurdish (Kurmanji)",
    "ckb": "Kurdish (Sorani)",
    "ky": "Kyrgyz",
    "lo": "Lao",
    "la": "Latin",
    "lv": "Latvian",
    "ln": "Lingala",
    "lt": "Lithuanian",
    "lg": "Luganda",
    "lb": "Luxembourgish",
    "mk": "Macedonian",
    "mai": "Maithili",
    "mg": "Malagasy",
    "ms": "Malay",
    "ml": "Malayalam",
    "mt": "Maltese",
    "mi": "Maori",
    "mr": "Marathi",
    "mni-Mtei": "Meiteilon (Manipuri)",
    "lus": "Mizo",
    "mn": "Mongolian",
    "my": "Myanmar (Burmese)",
    "ne": "Nepali",
    "no": "Norwegian",
    "or": "Odia (Oriya)",
    "om": "Oromo",
    "ps": "Pashto",
    "fa": "Persian",
    "pl": "Polish",
    "pt": "Portuguese",
    "pa": "Punjabi",
    "qu": "Quechua",
    "ro": "Romanian",
    "ru": "Russian",
    "sm": "Samoan",
    "sa": "Sanskrit",
    "gd": "Scots Gaelic",
    "nso": "Sepedi",
    "sr": "Serbian",
    "st": "Sesotho",
    "sn": "Shona",
    "sd": "Sindhi",
    "si": "Sinhala",
    "sk": "Slovak",
    "sl": "Slovenian",
    "so": "Somali",
    "es": "Spanish",
    "su": "Sundanese",
    "sw": "Swahili",
    "sv": "Swedish",
    "tg": "Tajik",
    "ta": "Tamil",
    "tt": "Tatar",
    "te": "Telugu",
    "th": "Thai",
    "ti": "Tigrinya",
    "ts": "Tsonga",
    "tr": "Turkish",
    "tk": "Turkmen",
    "ak": "Twi",
    "uk": "Ukrainian",
    "ur": "Urdu",
    "ug": "Uyghur",
    "uz": "Uzbek",
    "vi": "Vietnamese",
    "cy": "Welsh",
    "xh": "Xhosa",
    "yi": "Yiddish",
    "yo": "Yoruba",
    "zu": "Zulu"
} as const;

export const DeeplLanguages = {
    "": "Detect language",
    "ace": "Acehnese",
    "af": "Afrikaans",
    "sq": "Albanian",
    "ar": "Arabic",
    "an": "Aragonese",
    "hy": "Armenian",
    "as": "Assamese",
    "ay": "Aymara",
    "az": "Azerbaijani",
    "ba": "Bashkir",
    "eu": "Basque",
    "be": "Belarusian",
    "bn": "Bengali",
    "bho": "Bhojpuri",
    "bs": "Bosnian",
    "br": "Breton",
    "bg": "Bulgarian",
    "my": "Burmese",
    "yue": "Cantonese",
    "ca": "Catalan",
    "ceb": "Cebuano",
    "zh-hans": "Chinese (Simplified)",
    "zh-hant": "Chinese (Traditional)",
    "hr": "Croatian",
    "cs": "Czech",
    "da": "Danish",
    "prs": "Dari",
    "nl": "Dutch",
    "en-us": "English (American)",
    "en-gb": "English (British)",
    "eo": "Esperanto",
    "et": "Estonian",
    "fi": "Finnish",
    "fr": "French",
    "gl": "Galician",
    "ka": "Georgian",
    "de": "German",
    "el": "Greek",
    "gn": "Guarani",
    "gu": "Gujarati",
    "ht": "Haitian Creole",
    "ha": "Hausa",
    "he": "Hebrew",
    "hi": "Hindi",
    "hu": "Hungarian",
    "is": "Icelandic",
    "ig": "Igbo",
    "id": "Indonesian",
    "ga": "Irish",
    "it": "Italian",
    "ja": "Japanese",
    "jv": "Javanese",
    "ko": "Korean",
    "pam": "Kapampangan",
    "kk": "Kazakh",
    "kmr": "Kurdish (Kurmanji)",
    "ckb": "Kurdish (Sorani)",
    "ky": "Kyrgyz",
    "lo": "Lao",
    "la": "Latin",
    "lv": "Latvian",
    "ln": "Lingala",
    "lt": "Lithuanian",
    "lmo": "Lombard",
    "lb": "Luxembourgish",
    "mk": "Macedonian",
    "mai": "Maithili",
    "mg": "Malagasy",
    "ms": "Malay",
    "ml": "Malayalam",
    "mt": "Maltese",
    "mi": "Maori",
    "mr": "Marathi",
    "mn": "Mongolian",
    "ne": "Nepali",
    "nb": "Norwegian (Bokmål)",
    "oc": "Occitan",
    "om": "Oromo",
    "pag": "Pangasinan",
    "ps": "Pashto",
    "fa": "Persian",
    "pl": "Polish",
    "pt-br": "Portuguese (Brazilian)",
    "pt-pt": "Portuguese (European)",
    "pa": "Punjabi",
    "qu": "Quechua",
    "ro": "Romanian",
    "ru": "Russian",
    "sm": "Samoan",
    "sa": "Sanskrit",
    "gd": "Scottish Gaelic",
    "sr": "Serbian",
    "st": "Sesotho",
    "sn": "Shona",
    "scn": "Sicilian",
    "sd": "Sindhi",
    "si": "Sinhala",
    "sk": "Slovak",
    "sl": "Slovenian",
    "so": "Somali",
    "es": "Spanish",
    "es-419": "Spanish (Latin American)",
    "su": "Sundanese",
    "sw": "Swahili",
    "sv": "Swedish",
    "tl": "Tagalog",
    "tg": "Tajik",
    "ta": "Tamil",
    "tt": "Tatar",
    "te": "Telugu",
    "th": "Thai",
    "ti": "Tigrinya",
    "ts": "Tsonga",
    "tn": "Tswana",
    "tr": "Turkish",
    "tk": "Turkmen",
    "uk": "Ukrainian",
    "ur": "Urdu",
    "ug": "Uyghur",
    "uz": "Uzbek",
    "vi": "Vietnamese",
    "cy": "Welsh",
    "wo": "Wolof",
    "xh": "Xhosa",
    "yi": "Yiddish",
    "yo": "Yoruba",
    "zu": "Zulu"
} as const;

export function deeplLanguageToGoogleLanguage(language: string) {
    switch (language) {
        case "": return "auto";
        case "nb": return "no";
        case "zh-hans": return "zh-CN";
        case "zh-hant": return "zh-TW";
        case "en-us":
        case "en-gb":
            return "en";
        case "pt-br":
        case "pt-pt":
            return "pt";
        default:
            return language;
    }
}

// Generated using:
// curl https://translate.kagi.com/api/list-languages | jq 'map({(.language | ascii_downcase): .name}) | add | to_entries | sort_by(.value) | from_entries'
// Add "auto": "Detect language", as Kagi has no "language" for Detect Language, however anything not
// in the list will work as auto. Using "auto" will have this work using Google Translate defaults.
// Retrieved 2026-05-02. Meme languages and redundant entries removed.
export const KagiLanguages = {
    "auto": "Detect language",
    "om": "(Afan)/Oromoor/Oriya",
    "ab": "Abkhazian",
    "ace": "Acehnese",
    "ach": "Acholi",
    "aa": "Afar",
    "af": "Afrikaans",
    "ak": "Akan",
    "sq": "Albanian",
    "alz": "Alur",
    "am": "Amharic",
    "ar": "Arabic",
    "an": "Aragonese",
    "hy": "Armenian",
    "as": "Assamese",
    "av": "Avaric",
    "ae": "Avestan",
    "ava": "Awadhi",
    "ay": "Aymara",
    "az": "Azerbaijani",
    "ban": "Balinese",
    "bal": "Baluchi",
    "bm": "Bambara",
    "bci": "Baoulé",
    "ba": "Bashkir",
    "eu": "Basque",
    "btk": "Batak",
    "bar": "Bavarian",
    "be": "Belarusian",
    "bem": "Bemba",
    "bn": "Bengali",
    "bew": "Betawi",
    "bho": "Bhojpuri",
    "dz": "Bhutani",
    "bh": "Bihari",
    "bcl": "Bikol",
    "bi": "Bislama",
    "bs": "Bosnian",
    "br": "Breton",
    "bg": "Bulgarian",
    "my": "Burmese",
    "bua": "Buryat",
    "km": "Cambodian",
    "yue": "Cantonese",
    "ca": "Catalan",
    "ceb": "Cebuano",
    "ch": "Chamorro",
    "ce": "Chechen",
    "chr": "Cherokee",
    "ny": "Chichewa",
    "zh_cn": "Chinese (Simplified)",
    "zh_tw": "Chinese (Traditional)",
    "cu": "Church Slavonic",
    "chk": "Chuukese",
    "cv": "Chuvash",
    "kw": "Cornish",
    "co": "Corsican",
    "cr": "Cree",
    "crh": "Crimean Tatar",
    "hr": "Croatian",
    "cs": "Czech",
    "da": "Danish",
    "din": "Dinka",
    "div": "Divehi",
    "doi": "Dogri",
    "dov": "Dombe",
    "nl": "Dutch",
    "dyu": "Dyula",
    "en_gb": "English (UK)",
    "en_us": "English (US)",
    "eo": "Esperanto",
    "et": "Estonian",
    "ee": "Ewe",
    "fo": "Faroese",
    "fj": "Fiji",
    "fil": "Filipino",
    "fi": "Finnish",
    "fon": "Fon",
    "fr": "French",
    "fy": "Frisian",
    "fur": "Friulian",
    "ff": "Fulah",
    "gaa": "Ga",
    "gl": "Galician",
    "lg": "Ganda",
    "ka": "Georgian",
    "de": "German",
    "hac": "Gorani (Hawrami)",
    "el": "Greek",
    "kl": "Greenlandic",
    "gn": "Guarani",
    "gu": "Gujarati",
    "ht": "Haitian",
    "ha": "Hausa",
    "haw": "Hawaiian",
    "he": "Hebrew",
    "hz": "Herero",
    "hil": "Hiligaynon",
    "hi": "Hindi",
    "ho": "Hiri Motu",
    "hu": "Hungarian",
    "hrx": "Hunsrik",
    "iba": "Iban",
    "is": "Icelandic",
    "io": "Ido",
    "ig": "Igbo",
    "ilo": "Ilocano",
    "id": "Indonesian",
    "ia": "Interlingua",
    "ie": "Interlingue",
    "iu": "Inuktitut",
    "ik": "Inupiak",
    "ga": "Irish Gaelic",
    "it": "Italian",
    "jam": "Jamaican Patois",
    "ja": "Japanese",
    "jv": "Javanese",
    "cpe": "Jingpo",
    "kn": "Kannada",
    "kr": "Kanuri",
    "ks": "Kashmiri",
    "kk": "Kazakh",
    "kha": "Khasi",
    "ki": "Kikuyu",
    "rw": "Kinyarwanda",
    "ky": "Kirghiz",
    "rn": "Kirundi",
    "ktu": "Kituba",
    "kv": "Komi",
    "kg": "Kongo",
    "kok": "Konkani",
    "ko": "Korean",
    "kj": "Kuanyama",
    "ku": "Kurdish (Kurmanji)",
    "ckb": "Kurdish (Sorani)",
    "sdh": "Kurdish (Xwarin)",
    "lo": "Laothian",
    "ltg": "Latgalian",
    "la": "Latin",
    "lv": "Latvian",
    "lij": "Ligurian",
    "li": "Limburgan",
    "lim": "Limburgish",
    "ln": "Lingala",
    "lt": "Lithuanian",
    "lmo": "Lombard",
    "lu": "Luba_Katanga",
    "luo": "Luo",
    "lb": "Luxembourgish",
    "mk": "Macedonian",
    "mad": "Madurese",
    "mai": "Maithili",
    "mak": "Makassar",
    "mg": "Malagasy",
    "ms": "Malay",
    "ml": "Malayalam",
    "mt": "Maltese",
    "gv": "Manx Gaelic",
    "mi": "Maori",
    "mr": "Marathi",
    "mh": "Marshallese",
    "mwr": "Marwadi",
    "mo": "Moldavian",
    "mn": "Mongolian",
    "cnr": "Montenegrin",
    "nqo": "NKo",
    "nhe": "Nahuatl",
    "na": "Nauru",
    "nv": "Navajo",
    "ndc": "Ndau",
    "ng": "Ndonga",
    "new": "Nepalbhasa",
    "ne": "Nepali",
    "nd": "North Ndebele",
    "se": "Northern Sami",
    "ns": "Northern Sotho",
    "no": "Norwegian",
    "nb": "Norwegian (Bokmål)",
    "nn": "Norwegian (Nynorsk)",
    "nus": "Nuer",
    "oc": "Occitan",
    "oj": "Ojibwa",
    "or": "Oriya",
    "os": "Ossetian",
    "pi": "Pali",
    "pag": "Pangasinan",
    "pap": "Papiamento",
    "ps": "Pashto/Pushto",
    "fa": "Persian",
    "pl": "Polish",
    "pt_br": "Portuguese (Brazil)",
    "pt_pt": "Portuguese (Portugal)",
    "pa": "Punjabi",
    "qu": "Quechua",
    "kek": "Qʼeqchiʼ",
    "rm": "Rhaeto_Romanic",
    "rom": "Romani",
    "ro": "Romanian",
    "ru": "Russian",
    "sm": "Samoan",
    "sg": "Sangro",
    "sa": "Sanskrit",
    "sat": "Santali",
    "sc": "Sardinian",
    "sco": "Scots",
    "gd": "Scottish Gaelic",
    "sr": "Serbian",
    "sh": "Serbo_Croatian",
    "st": "Sesotho",
    "seto": "Seto",
    "shn": "Shan",
    "sn": "Shona",
    "ii": "Sichuan Yi",
    "scn": "Sicilian",
    "szl": "Silesian",
    "sd": "Sindhi",
    "si": "Sinhalese",
    "ss": "Siswati",
    "sk": "Slovak",
    "sl": "Slovenian",
    "so": "Somali",
    "sb": "Sorbian",
    "nr": "South Ndebele",
    "sma": "Southern Sami",
    "es": "Spanish",
    "su": "Sundanese",
    "sus": "Susu",
    "sx": "Sutu",
    "sw": "Swahili",
    "sv": "Swedish",
    "gsw": "Swiss German",
    "syr": "Syriac",
    "tl": "Tagalog",
    "ty": "Tahitian",
    "tg": "Tajik",
    "ta": "Tamil",
    "tt": "Tatar",
    "te": "Telugu",
    "tet": "Tetum",
    "th": "Thai",
    "bo": "Tibetan",
    "ti": "Tigrinya",
    "tiv": "Tiv",
    "tpi": "Tok Pisin",
    "to": "Tonga",
    "ts": "Tsonga",
    "tn": "Tswana",
    "tum": "Tumbuka",
    "tr": "Turkish",
    "tk": "Turkmen",
    "tyv": "Tuvan",
    "tw": "Twi",
    "udm": "Udmurt",
    "ug": "Uighur",
    "uk": "Ukrainian",
    "ur": "Urdu",
    "uz": "Uzbek",
    "ve": "Venda",
    "vec": "Venetian",
    "vi": "Vietnamese",
    "vo": "Volapuk",
    "vro": "Võro",
    "wa": "Walloon",
    "war": "Waray",
    "cy": "Welsh",
    "wo": "Wolof",
    "xh": "Xhosa",
    "sah": "Yakut",
    "yi": "Yiddish",
    "yo": "Yoruba",
    "yua": "Yucatec Maya",
    "zap": "Zapotec",
    "diq": "Zazaki",
    "za": "Zhuang",
    "zu": "Zulu"
} as const;
